// Maße deterministisch aus dem Klartext lesen, statt sie dem Modell zu überlassen: Fall 6a608b33
// ("MSH 70 x 4") lieferte in drei identischen Läufen drei verschiedene Geometrien, und daran hängt
// über den Maßfilter der ganze Artikel. Der Extraktions-Prompt sagt "two numbers is a round pipe"
// und kennt für die Hohlprofil-Kürzel keine Ausnahme — das Modell schwankt zwischen Regel und
// Fachwissen. Überschrieben wird nur, was hier sicher lesbar ist; alles andere bleibt beim Modell.

/** Die vier Maßfelder, die eine Abmessungszeile bestimmen kann. */
export interface ParsedDimensions {
  outerDiameter1: number;
  /** Zweites Außenmaß (Höhe) bei Hohlprofilen; null bei Rundrohren. */
  outerDiameter2: number | null;
  wallThickness: number | null;
  innerDiameter: number | null;
}

// Bei diesen Kürzeln meint die Kurzform "A x B" ein Quadratprofil A × A mit Wand B, kein Rundrohr.
const HOLLOW_SECTION_ARTICLE =
  /\bMSH\b|\bSHP\b|\bQRO\b|\bRRO\b|\bKHP\b|\bWHP\b|\bVKT\b|hohlprofil|vierkant|quadratrohr|rechteckrohr/i;

// Präfixe, die den Zahlenwerten nichts hinzufügen.
const LEADING_NOISE = /^(?:ca\.|Ø|D\.|Drm\.|AD|D)\s*/i;
const TRAILING_UNIT = /\s*mm\s*$/i;
// "D323,9/301,9 (RM)" — RM steht fuer Rohmass und ist eine Anmerkung, kein Wert.
const TRAILING_NOTE = /\s*\([^)]*\)\s*$/;

// Toleranzen und Bereiche bleiben liegen — sie ändern die Bedeutung der Zahlen.
const CARRIES_TOLERANCE = /[+±]|-\s*\d|\bbis\b|\.\.\./i;

const NUMBER = String.raw`\d+(?:[.,]\d+)?`;
const THREE_NUMBERS = new RegExp(`^(${NUMBER})\\s*[x×/]\\s*(${NUMBER})\\s*[x×/]\\s*(${NUMBER})$`, 'i');
const TWO_NUMBERS = new RegExp(`^(${NUMBER})\\s*[x×/]\\s*(${NUMBER})$`, 'i');

/** "160/80/8,0" und "6.3" meinen dasselbe Trennzeichen — im Maßkontext ist beides ein Komma. */
function toNumber(raw: string): number {
  return Number(raw.replace(',', '.'));
}

/**
 * Wandstärke oder Innendurchmesser? Physikalisch entscheidbar: eine Wand erreicht höchstens den
 * halben Außendurchmesser. "D273/255,4" meint also den Innendurchmesser, "254.0 / 8.0" die Wand.
 */
function isInnerDiameter(outer: number, second: number): boolean {
  return second >= outer / 2;
}

/**
 * Liest eine Abmessungszeile; null bei jeder unbekannten Form. `articleContext` ist der
 * Positionsblock — daran hängt, ob "A x B" ein Quadratprofil oder ein Rundrohr meint.
 */
export function parseDimensionText(dimensionText: string, articleContext: string): ParsedDimensions | null {
  const trimmed = dimensionText.trim();
  if (CARRIES_TOLERANCE.test(trimmed)) return null;

  const normalized = trimmed.replace(TRAILING_NOTE, '').replace(LEADING_NOISE, '').replace(TRAILING_UNIT, '').trim();

  const three = THREE_NUMBERS.exec(normalized);
  if (three?.[1] !== undefined && three[2] !== undefined && three[3] !== undefined) {
    // Drei Zahlen sind immer Breite × Höhe × Wand — ein Rundrohr hat kein drittes Maß.
    return {
      outerDiameter1: toNumber(three[1]),
      outerDiameter2: toNumber(three[2]),
      wallThickness: toNumber(three[3]),
      innerDiameter: null,
    };
  }

  const two = TWO_NUMBERS.exec(normalized);
  if (two?.[1] === undefined || two[2] === undefined) return null;

  const outer = toNumber(two[1]);
  const second = toNumber(two[2]);
  if (outer <= 0 || second <= 0) return null;

  if (isInnerDiameter(outer, second)) {
    return {
      outerDiameter1: outer,
      outerDiameter2: null,
      wallThickness: Math.round(((outer - second) / 2) * 100) / 100,
      innerDiameter: second,
    };
  }

  if (HOLLOW_SECTION_ARTICLE.test(articleContext)) {
    // "MSH 70 x 4" heißt 70 × 70 × 4 — rechteckige Profile stehen immer dreistellig da.
    return { outerDiameter1: outer, outerDiameter2: outer, wallThickness: second, innerDiameter: null };
  }

  return { outerDiameter1: outer, outerDiameter2: null, wallThickness: second, innerDiameter: null };
}

/** Je Positionsnummer der zugehörige Block aus dem Klartext von Schritt 1.5. */
export function readPositionBlocks(canonicalDocument: string): Map<string, string> {
  const result = new Map<string, string>();

  for (const block of canonicalDocument.split(/^### Position /m).slice(1)) {
    const positionNumber = /^\s*(\S+)/.exec(block)?.[1];
    if (positionNumber !== undefined) result.set(positionNumber, block);
  }

  return result;
}

/** Eine "- <Feld>: <Wert>"-Zeile aus einem Positionsblock, oder null. */
export function readPositionField(block: string, label: string): string | null {
  return new RegExp(`^- ${label}:\\s*(.+)$`, 'm').exec(block)?.[1]?.trim() ?? null;
}

// "17 HL" sind 17 ganze Stangen, keine 17 Meter — ohne diese Regel teilt calculateQuantity die
// Zahl durch die Handelslänge und macht aus 17 eine 3.
const BAR_COUNT = /^(\d+)\s*(HL|LGL|Handelsl(?:ä|ae)ngen?|Lagerl(?:ä|ae)ngen?|Stangen?|Stg\.?)$/i;

// Nur diese Wörter sagen zusätzlich, dass es HANDELSlängen sind. "Stange" zählt Stücke, sagt aber
// nichts über die Längenart — sonst würde ein Zuschnitt "2 Stg." fälschlich zur Handelslänge.
const TRADE_LENGTH_WORD = /^(?:HL|LGL|Handelsl|Lagerl)/i;

/** Stückzahl aus einer Mengenzeile der Form "<N> HL" / "<N> Stg.", sonst null. */
export function parseBarCount(quantityText: string): { count: number; isTradeLength: boolean } | null {
  const match = BAR_COUNT.exec(quantityText.trim());
  if (match?.[1] === undefined || match[2] === undefined) return null;
  return { count: Number(match[1]), isTradeLength: TRADE_LENGTH_WORD.test(match[2]) };
}

// "Handelslängen 5-7m" neben einer eigenen Stücklänge ist ein allgemeiner Hinweis des Kunden und
// keine Toleranz dieser Position. Schritt 1.5 dupliziert ihn in jede Position, weshalb er sonst
// überall als min/max landet. Die echte Spanne kommt aus dem Stammsatz, wenn die Position gar
// keine Länge nennt.
const TRADE_LENGTH_NOTE =
  /(?:HL|H(?:a|ä|ae)ndelsl(?:ä|ae)ngen?|Lagerl(?:ä|ae)ngen?)\s*\.?\s*(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)/i;

/** Werte unter 1000 sind Meter, darüber schon Millimeter. */
function noteLengthToMm(raw: string): number {
  const value = Number(raw.replace(',', '.'));
  return value < 1000 ? Math.round(value * 1000) : Math.round(value);
}

/** Die Spanne aus einem Handelslängen-Hinweis des Positionsblocks, sonst null. */
export function parseTradeLengthNote(block: string): { min: number; max: number } | null {
  const match = TRADE_LENGTH_NOTE.exec(block);
  if (match?.[1] === undefined || match[2] === undefined) return null;
  return { min: noteLengthToMm(match[1]), max: noteLengthToMm(match[2]) };
}
